import { LevelType } from '../types/Level';
import { DraftLevel } from './types';

// Finding a real photo for levels that show a real thing. Wikimedia Commons is searched from the
// browser because it allows both the search and the picture download from a web page; Google's
// results can only be opened in a new tab, since Google offers no search API to new apps.

export interface FoundPicture {
  title: string;
  imageUrl: string;
  width: number;
  height: number;
  // The Commons page for the file, with its full licensing details
  pageUrl: string;
  author: string;
  license: string;
}

export const canFindPicture = (type: LevelType): boolean =>
  type === LevelType.WhatsInThePicture || type === LevelType.WhereIsThisLocated;

export const searchQueryFor = (draft: DraftLevel): string =>
  (draft.levelType === LevelType.WhereIsThisLocated
    ? `${draft.locationName || ''} ${draft.sentence || ''}`
    : draft.sentence || ''
  ).trim();

export const googleImagesUrl = (query: string): string =>
  `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(query)}`;

const RESULT_LIMIT = 30;
const PICTURE_WIDTH = 960;

export const commonsSearchUrl = (query: string): string =>
  'https://commons.wikimedia.org/w/api.php?action=query&format=json&origin=*' +
  `&generator=search&gsrnamespace=6&gsrlimit=${RESULT_LIMIT}&gsrsearch=${encodeURIComponent(query)}` +
  `&prop=imageinfo&iiprop=${encodeURIComponent('url|mime|extmetadata')}&iiurlwidth=${PICTURE_WIDTH}`;

// Photos only: a level picture cannot be a vector drawing, a document, a video or an animation
const USABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const plainText = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim() : '';

const metadataValue = (metadata: unknown, key: string): string => {
  const entry = metadata && typeof metadata === 'object' ? (metadata as Record<string, { value?: unknown }>)[key] : undefined;
  return plainText(entry?.value);
};

export const parseCommonsResults = (json: unknown): FoundPicture[] => {
  const pages = json && typeof json === 'object'
    ? (json as { query?: { pages?: Record<string, unknown> } }).query?.pages
    : undefined;
  if (!pages || typeof pages !== 'object') return [];

  const entries = Object.keys(pages)
    .map(key => pages[key] as { title?: unknown; index?: unknown; imageinfo?: unknown })
    .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));

  const pictures: FoundPicture[] = [];
  entries.forEach(entry => {
    const info = Array.isArray(entry.imageinfo) ? (entry.imageinfo[0] as Record<string, unknown>) : undefined;
    if (!info || typeof info.thumburl !== 'string' || USABLE_TYPES.indexOf(String(info.mime)) === -1) return;

    pictures.push({
      title: String(entry.title || '').replace(/^File:/, '').replace(/\.[^.]+$/, ''),
      imageUrl: info.thumburl,
      width: Number(info.thumbwidth) || 0,
      height: Number(info.thumbheight) || 0,
      pageUrl: typeof info.descriptionurl === 'string' ? info.descriptionurl : '',
      author: metadataValue(info.extmetadata, 'Artist'),
      license: metadataValue(info.extmetadata, 'LicenseShortName'),
    });
  });
  return pictures;
};

type FetchJson = (url: string) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

export const searchCommons = async (query: string, fetchFn: FetchJson = url => fetch(url)): Promise<FoundPicture[]> => {
  if (!query.trim()) return [];
  const response = await fetchFn(commonsSearchUrl(query.trim()));
  if (!response.ok) {
    throw new Error(`Wikimedia Commons search failed (${response.status}). Try again.`);
  }
  return parseCommonsResults(await response.json());
};

// The credit line most Commons licenses ask for.
export const creditFor = (picture: FoundPicture): string =>
  [picture.author, picture.license, 'via Wikimedia Commons'].filter(part => part).join(', ');
